import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { IMAGES, LOGO_LOCKUP_URL, PHILOSOPHY } from "@/lib/assets";
import { galleryProjects } from "@/lib/galleryData";
import { useLanguage } from "@/i18n/LanguageContext";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } },
};
const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

const HERO_IMAGES = [
  IMAGES.hero,
  PHILOSOPHY.ambiance,
  galleryProjects[0]?.cover,
  IMAGES.interior,
].filter(Boolean);

export const Hero = () => {
  const { t } = useLanguage();
  const [bgIdx, setBgIdx] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setBgIdx((i) => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(id);
  }, []);
  const scrollDown = () =>
    document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0" data-testid="hero-bg">
        {HERO_IMAGES.map((src, i) => (
          <motion.img
            key={src}
            src={src}
            alt=""
            aria-hidden="true"
            initial={false}
            animate={{ opacity: i === bgIdx ? 1 : 0, scale: i === bgIdx ? 1.06 : 1 }}
            transition={{
              opacity: { duration: 1.8, ease: "easeInOut" },
              scale: { duration: 6.4, ease: "linear" },
            }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ))}
        <div className="absolute inset-0 bg-[#0A1420]/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1420] via-[#0A1420]/40 to-[#0A1420]/70" />
      </div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 max-w-5xl mx-auto px-6 text-center"
      >
        <motion.img
          variants={item}
          src={LOGO_LOCKUP_URL}
          alt="Medernach Lionel Designer"
          data-testid="hero-logo"
          className="mx-auto mb-12 w-72 sm:w-[28rem] lg:w-[36rem] object-contain drop-shadow-[0_8px_40px_rgba(212,175,55,0.25)]"
        />

        <motion.p
          variants={item}
          className="text-[#D4AF37] tracking-[0.35em] uppercase text-xs sm:text-sm mb-8"
        >
          {t("hero.eyebrow")}
        </motion.p>

        <motion.h1
          variants={item}
          className="font-serif-lux text-5xl sm:text-6xl lg:text-8xl leading-[0.95] text-white"
        >
          {t("hero.title1")}
          <br />
          <span className="gold-gradient-text italic">{t("hero.title2")}</span>
        </motion.h1>

        <motion.p
          variants={item}
          className="mt-10 max-w-2xl mx-auto text-[#A0ABC0] text-base sm:text-lg font-light leading-relaxed"
        >
          {t("hero.subtitle")}
        </motion.p>

        <motion.div variants={item} className="mt-12 flex flex-col sm:flex-row gap-5 justify-center">
          <button
            onClick={() => document.querySelector("#consultation")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-cta-primary"
            className="bg-[#D4AF37] text-[#0A1420] font-semibold hover:bg-[#E6C27A] transition-colors duration-300 px-9 py-4 uppercase tracking-widest text-sm"
          >
            {t("hero.ctaPrimary")}
          </button>
          <button
            onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-cta-secondary"
            className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1420] transition-colors duration-300 px-9 py-4 uppercase tracking-widest text-sm"
          >
            {t("hero.ctaSecondary")}
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ opacity: { delay: 1.5 }, y: { repeat: Infinity, duration: 2 } }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 text-[#D4AF37] z-10"
        data-testid="hero-scroll-indicator"
        aria-label="Défiler vers le bas"
      >
        <ChevronDown size={30} />
      </motion.button>
    </section>
  );
};
